//region imports
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import TaskView from './TaskView.component.jsx';
import TasksActionButtons from './MyTasksButtonsBarView.component.jsx';
import TaskListHeader from './TaskListHeaderView.component.jsx';
import Search from './SearchView.component.jsx';
import '../pagelayout/global.scss';
import {ServerError} from '../errorpage/ErrorPage.component';
import translate from '../translations/translations.wrapper.jsx';
import {MyTasksActions} from './AvailableActions.class';
import {
    changeAllTaskSelection,
    changeCompanyFilter,
    changeSearchFilter,
    changeSort,
    changeTaskSelection,
    changeTasksSelection,
    loadTasks,
    saveScrollPosition,
    changeDisplayedDate,
    changeAutopayURL,
    changeVisibleOrder
} from './myTasks.action';

import {loadHistory} from '../myhistory/myHistory.action';
import {
    actionFinished,
    changeOneHandleOnlyFlag,
    taskAction,
    changeTryToLoopTasksFlag
} from './action/taskAction.action';
import * as reduxSelectors from '../store/application.reducers';
import {login} from "utils/api/odp.api";
import {LOADING_STATUS, MODAL_ACTIONS, TASK_ACTIONS} from "utils/constants";
import * as Api from "utils/api/api";
import * as logger from "utils/logger.function";
import {ModalContext} from "components/modals/ModalContext";
import {filterTasks} from './taskListFiltering.function';
import {ownTaskGroups, toReviewWrappers, withReviewGroup} from './tasksOnReview.function';
import {keyPressHandler} from "utils/keyPressHandler.function";
import {snowplowTrackEvent} from "utils/snowplow";
import AsyncTaskActionExecutor from './AsyncTaskActionExecutor.component';
import TableFooter from "components/resizableTable/TableFooter.component.tsx";
import {Notification, NotificationType} from "components/common/Notification.component";
import NewPaymentBatch from "components/mytasks/NewPaymentBatch.component";
import { Loader, LoaderSize } from "components/common/Loader.component.tsx";
import { launchDarkly } from "utils/launchDarkly";
import TasksGrid from 'components/mytasks/TasksGrid.component';
import BetaSwitch from './BetaSwitch.component';
import {
    groupWrappers,
    approvableKeys,
    readStoredGroupBy,
    writeStoredGroupBy
} from './tasksGrouping.function';
import {sumTotalAmount} from './taskAmounts.function';
import {isBetaEnabled, writeStoredBeta} from './betaVersion.function';
import {readStoredBoolean, writeStoredBoolean} from 'utils/localStorage.function';
import {
    COLUMN_FIELDS,
    fieldLabel,
    readStoredColumns,
    readStoredLayout,
    readStoredSort,
    resolveVisibleFields,
    toggleField,
    visibleTaskOrder,
    writeStoredColumns,
    writeStoredLayout,
    writeStoredSort
} from './tasksGrid.function';


export const COMPANY_FILTER_STORAGE_KEY = '@@myTasks/onlyCurrentCompany';

export class MyTasks extends Component {

    // the reassign dialog for a review is opened straight from here: the review is no task in the
    // store, so it cannot go through taskAction like the user's own tasks do
    static contextType = ModalContext;

    constructor(props) {
        super(props);
        this.sortTasks = this.sortTasks.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.toggleTask = this.toggleTask.bind(this);
        this.toggleGroup = this.toggleGroup.bind(this);
        this.approveGroup = this.approveGroup.bind(this);
        this.toggleGroupSelection = this.toggleGroupSelection.bind(this);
        this.toggleColumn = this.toggleColumn.bind(this);
        this.resetColumns = this.resetColumns.bind(this);
        this.changeColumnLayout = this.changeColumnLayout.bind(this);
        this.toggleBeta = this.toggleBeta.bind(this);
        this.changeGroupBy = this.changeGroupBy.bind(this);
        this.changeGridSort = this.changeGridSort.bind(this);
        this.reportVisibleOrder = this.reportVisibleOrder.bind(this);
        this.exportCsv = this.exportCsv.bind(this);
        this.changeCompanyFilter = this.changeCompanyFilter.bind(this);
        this.loadReviewTasks = this.loadReviewTasks.bind(this);
        this.reassignReview = this.reassignReview.bind(this);
        // the grid publishes its CSV export here; the button for it sits in the filter bar
        this.gridExport = React.createRef();
        // the grouping the grid last rendered, kept so the order reported back matches it
        this.groups = null;
        this.scrollContainer = React.createRef();
        this.list = React.createRef();

        // local UI state for the flag-gated overview (grouping/columns)
        this.state = {
            // the grouping and the sort stay where the user put them, opening a task and coming back included
            groupBy: readStoredGroupBy(),
            // null = the sort the grid opens on; an array = the columns the user sorted by
            gridSort: readStoredSort(),
            collapsedGroups: {},
            // null = show the default columns; an array = the user picked their own columns
            selectedColumns: readStoredColumns(),
            // null = the columns as they come; an object = the order and widths the user dragged them into
            columnLayout: readStoredLayout(),
            // the new version is on unless the user switched it off
            betaEnabled: isBetaEnabled(),
            // the tasks the user sent on review, as the list's wrappers (see tasksOnReview.function)
            reviewWrappers: []
        };
    }

    /**
     * the reviews the user sent - they are someone else's tasks, so they are kept here rather than
     * in the store, where everything is the user's own to select, open and act on
     */
    loadReviewTasks() {
        if (!this.props.showTasksOnReview) return;
        Api.getMyTasksOnReview().then(response => {
            if (this.unmounted) return;
            this.setState({reviewWrappers: toReviewWrappers(response?.tasks)});
        }).catch(error => {
            // the user's own tasks are what the page is for, so a failure here only loses the extra rows -
            // dropped rather than kept, as after a reassign the old ones name a reviewer the task has left
            if (!this.unmounted) this.setState({reviewWrappers: []});
            logger.debug("MyTasks", "could not load the tasks sent on review: " + (error?.message ?? error));
        });
    }

    /**
     * hand a review over to another reviewer; it comes back as a new task, so the reviews are read again
     */
    reassignReview(task) {
        this.context?.toggleModal(MODAL_ACTIONS.REASSIGN_REVIEW, {task, onReassigned: this.loadReviewTasks});
        this.trackEvent('reassign_review', 'open dialog');
    }

    /**
     * the reviews the user sent, under the same search and company filter as their own tasks
     */
    getReviewWrappers() {
        if (!this.isOverviewEnabled() || !this.props.showTasksOnReview) return [];
        const onlyForCompany = this.props.company ? this.props.currentContextId : null;
        return filterTasks(this.state.reviewWrappers, this.props.search, onlyForCompany);
    }

    /**
     * the Beta switch picks between the new overview and the old flat list
     */
    toggleBeta() {
        this.setState(state => ({betaEnabled: !state.betaEnabled}), () => {
            const betaEnabled = this.state.betaEnabled;
            writeStoredBeta(betaEnabled);

            // which way the switch was flipped, the drop back to the old list above all
            this.trackEvent('version_switch', betaEnabled ? 'old_to_beta' : 'beta_to_old');

            // the flat list renders the tasks in the store's own order, so the grid's goes with it
            if (!betaEnabled) this.props.changeVisibleOrder([]);
        });
    }

    /**
     * usage of the beta overview, to see which of its controls people actually reach for
     */
    trackEvent(action, label) {
        snowplowTrackEvent({category: 'my_tasks', action, label}, this.props.userConfiguration);
    }

    /**
     * download the grid's rows as CSV; there is nothing to download before the grid is up
     */
    exportCsv() {
        if (!this.gridExport.current) return;
        this.gridExport.current();
        this.trackEvent('export_csv', 'export to CSV');
    }

    changeGroupBy(groupBy) {
        writeStoredGroupBy(groupBy);
        this.setState({groupBy});
        this.trackEvent('group_by', groupBy);
    }

    // only the beta list remembers the choice across logout
    changeCompanyFilter(onlyCurrentCompany) {
        if (this.isOverviewEnabled()) writeStoredBoolean(COMPANY_FILTER_STORAGE_KEY, onlyCurrentCompany);
        this.props.changeCompanyFilter(onlyCurrentCompany);
    }

    restoreCompanyFilter() {
        const stored = readStoredBoolean(COMPANY_FILTER_STORAGE_KEY);
        if (stored !== null && stored !== Boolean(this.props.company)) {
            this.props.changeCompanyFilter(stored);
        }
    }

    changeGridSort(gridSort) {
        writeStoredSort(gridSort);
        this.setState({gridSort});
    }

    /**
     * the grid reports the task rows it shows, in its own order; what the store keeps is the whole
     * list in that order - a collapsed group holds rows the grid has none of
     */
    reportVisibleOrder(sortedKeys) {
        this.props.changeVisibleOrder(visibleTaskOrder(ownTaskGroups(this.getGroups()), sortedKeys));
    }

    /**
     * whether the grouped overview is the list currently on screen
     */
    isOverviewEnabled() {
        return Boolean(this.props.showBetaSwitch && this.state.betaEnabled);
    }

    getGroups() {
        const filteredTasks = this.props.tasks.filteredTasks;
        const groupBy = this.state.groupBy;
        const reviewWrappers = this.getReviewWrappers();
        // the reported order has to come from the grouping the grid rendered - a fresh Date.now() can bucket urgency differently
        if (!this.groups || this.groups.filteredTasks !== filteredTasks || this.groups.groupBy !== groupBy ||
            !sameWrappers(this.groups.reviewWrappers, reviewWrappers)) {
            this.groups = {
                filteredTasks,
                groupBy,
                reviewWrappers,
                groups: withReviewGroup(groupWrappers(filteredTasks, groupBy, Date.now()), reviewWrappers)
            };
        }
        return this.groups.groups;
    }

    toggleColumn(field) {
        const selectedColumns = toggleField(this.getVisibleColumns(), field);
        writeStoredColumns(selectedColumns);
        this.setState({selectedColumns});
    }

    resetColumns() {
        writeStoredColumns(null);
        writeStoredLayout(null);
        this.setState({selectedColumns: null, columnLayout: null});
    }

    /**
     * the grid reports the order and widths the user just dragged the columns into
     */
    changeColumnLayout(columnLayout) {
        writeStoredLayout(columnLayout);
        this.setState({columnLayout});
    }

    getVisibleColumns() {
        return resolveVisibleFields(this.state.selectedColumns);
    }

    /**
     * whether there is anything of the user's own to put back
     */
    isGridCustomised() {
        return this.state.selectedColumns !== null || this.state.columnLayout !== null;
    }

    /**
     * every column the picker can offer, flagged with whether it is currently shown
     */
    getColumnOptions() {
        const visible = new Set(this.getVisibleColumns());
        return COLUMN_FIELDS.map(field => ({
            field,
            label: fieldLabel(field, this.props.translate),
            isVisible: visible.has(field)
        }));
    }

    toggleGroup(key) {
        this.setState(prev => ({
            collapsedGroups: {...prev.collapsedGroups, [key]: !prev.collapsedGroups[key]}
        }));
    }

    approveGroup(wrappers) {
        const keys = approvableKeys(wrappers);
        if (keys.length > 0) {
            this.props.taskAction(TASK_ACTIONS.APPROVE, keys);
        }
    }

    componentDidMount() {

        //handle shortcut keys
        window.addEventListener('keydown', this.handleKeyPress);
        // start loading tasks
        this.props.loadTasks();
        this.loadReviewTasks();
        // reset the flag, allowing only one handle
        this.props.changeOneHandleOnlyFlag(true);
        //my tasks actions should not try to loop the tasks
        this.props.changeTryToLoopTasksFlag(false);

        this.props.changeAutopayURL(null);

        // reset the dialog if opened - it holds data about task, but data might have expired (for example when user changes the context),
        // so we should close the dialog each time user returns to myTasks
        this.props.actionFinished();

        // which of the two versions the user actually works in, counted once per visit
        if (this.props.showBetaSwitch) {
            this.trackEvent('version_used', this.state.betaEnabled ? 'beta' : 'old');
        }

        if (this.isOverviewEnabled()) this.restoreCompanyFilter();

        if (this.props.scrollTop && this.scrollContainer.current && this.scrollContainer.current.scrollTop) {
            this.scrollContainer.current.scrollTop = this.props.scrollTop;
        }
    }

    sortTasks(event) {
        this.props.changeSort(event);
    }

    componentWillUnmount() {
        this.unmounted = true;
        let scrollPosition = this.scrollContainer?.current?.scrollTop ? this.scrollContainer.current.scrollTop : 0;
        window.removeEventListener('keydown', this.handleKeyPress);
        this.props.saveScrollPosition(scrollPosition);
    }

    componentDidUpdate(prevProps, prevState) {
        if ((this.props.scrollTop || this.props.changeDisplayedDate) && this.list && this.scrollContainer.current && this.props.scrollTop > 0) {
            this.scrollContainer.current.scrollTop = this.props.scrollTop;
        }

        // beta switched on, or the flag arrived after mount
        const wasOverview = Boolean(prevProps.showBetaSwitch && prevState.betaEnabled);
        if (!wasOverview && this.isOverviewEnabled()) this.restoreCompanyFilter();
    }

    handleKeyPress(e) {
        e.stopPropagation();
        keyPressHandler(e, this.props.taskAction, this.props.tasks.selectedKeys, this.getActiveActions());
    }

    getActiveActions() {
        const activeActions = new MyTasksActions(this.props.tasks.selectedTasks);
        // with nothing selected, open means 'handle all', which an empty list has nothing for
        if (this.props.tasks.filteredTasks.length === 0) {
            activeActions.disable(TASK_ACTIONS.OPEN);
        }
        return activeActions;
    }

    toggleTask(event) {
        let scrollPosition = this.scrollList && this.scrollList.scrollTop ? this.scrollList.scrollTop : 0;
        this.props.saveScrollPosition(scrollPosition);
        this.props.changeTaskSelection(event);
    }

    /**
     * the group's own select all: selects every task of the group, or clears them once they all are
     */
    toggleGroupSelection(wrappers) {
        const allSelected = wrappers.every(wrapper => wrapper.selected);
        this.props.changeTasksSelection(wrappers.map(wrapper => wrapper.task.key), !allSelected);
    }

    getTasksAsComponent(wrappers = this.props.tasks.filteredTasks) {
        return wrappers.map(taskWrapper => {
            const task = taskWrapper.task;
            const taskProps = {
                task: task,
                translate: this.props.translate,
                selectCallback: this.toggleTask,
                selected: taskWrapper.selected,
                displayedDate: this.props.displayedDate,
                showLastChangedBy: this.props.showLastChangedBy
            };
            const taskActions = new MyTasksActions(task);
            // pass callback, only if actions are allowed
            if (taskActions.isEnabled(TASK_ACTIONS.APPROVE)) {
                taskProps.approveCallback = () => {
                    this.props.taskAction(TASK_ACTIONS.APPROVE, [task.key], {hasDraftComment: task.hasDraftComment});
                };
            }
            if (taskActions.isEnabled(TASK_ACTIONS.REJECT)) {
                taskProps.rejectCallback = () => {
                    this.props.taskAction(TASK_ACTIONS.REJECT, [task.key]);
                };
            }
            if (taskActions.isEnabled(TASK_ACTIONS.REVIEW)) {
                taskProps.reviewCallback = () => {
                    this.props.taskAction(TASK_ACTIONS.REVIEW, [task.key]);
                };
            }

            return (
                <TaskView
                    key={task.id}
                    {...taskProps}
                />);

        })
    }

    renderGrid() {
        const groups = this.getGroups();
        if (groups.length === 0) {
            return <NoTasksFound translate={this.props.translate}/>;
        }
        return (
            <TasksGrid
                groups={groups}
                collapsedGroups={this.state.collapsedGroups}
                visibleFields={this.getVisibleColumns()}
                columnLayout={this.state.columnLayout}
                onColumnLayoutChange={this.changeColumnLayout}
                sortState={this.state.gridSort}
                onSortStateChange={this.changeGridSort}
                onSortedKeysChange={this.reportVisibleOrder}
                translate={this.props.translate}
                exportRef={this.gridExport}
                allSelected={this.props.tasks.areAllSelected}
                onToggleAll={() => this.props.changeAllTaskSelection()}
                onToggleTask={this.toggleTask}
                onToggleGroup={this.toggleGroup}
                onToggleGroupSelection={this.toggleGroupSelection}
                onApproveGroup={this.approveGroup}
                taskAction={this.props.taskAction}
                quickViewEnabled={this.props.showQuickView}
                onReassignReview={this.reassignReview}
            />
        );
    }

    /**
     * nothing of the user's own, and no payment batch - though with the new version, the user
     * still sees the reviews they are waiting on
     */
    hasNothingToList(overviewEnabled) {
        const hasReviews = overviewEnabled && this.props.showTasksOnReview && this.state.reviewWrappers.length > 0;
        return this.props.tasks.allTasks.length === 0 && this.props.tasksWithPayment.length === 0 && !this.props.batchTasks && !hasReviews;
    }

    getContentClassName(overviewEnabled) {
        let myTasksClass = this.props.hiddenTasks > 0 ? "my-tasks-content hidden-tasks-warning" : "my-tasks-content";
        if (this.props.batchTasks) {
            myTasksClass += " batch-tasks-area";
        }
        if (overviewEnabled) {
            myTasksClass += " my-tasks-overview";
        }
        return myTasksClass;
    }

    // the old version: the flat list with its own header
    renderFlatList() {
        const tasksAsComponents = this.getTasksAsComponent();
        const content = tasksAsComponents.length > 0 ? tasksAsComponents :
            <NoTasksFound translate={this.props.translate}/>;

        return (
            <ul className="list-unstyled">
                <li className="task-list-container no-padding margin-bottom app-width">
                    <div className="task-header app-width">
                        <TaskListHeader
                            sortByCallback={this.sortTasks}
                            selectAllCallback={this.props.changeAllTaskSelection}
                            currentSort={this.props.sort}
                            translate={this.props.translate}
                            currentAllSelected={this.props.tasks.areAllSelected}
                            displayedDate={this.props.displayedDate}
                            showLastChangedBy={this.props.showLastChangedBy}
                        />
                    </div>

                    <div className="task-list-scroll-wrapper" ref={this.scrollContainer}>
                        <ul className="task-list-content" aria-live="polite" ref={elem => {
                            this.list = elem && elem.getBoundingClientRect();
                        }}>
                            {content}
                        </ul>
                    </div>
                </li>
            </ul>
        );
    }

    renderTasks() {
        let activeActions = this.getActiveActions();

        // flags
        const oneCompanyOnlyUser = this.props.availableContextsCount === 1;


        const overviewEnabled = this.isOverviewEnabled();

        // pinned to the bottom left of the page, in the footer strip
        const betaSwitch = this.props.showBetaSwitch &&
            <BetaSwitch
                isChecked={this.state.betaEnabled}
                onChange={this.toggleBeta}
                translate={this.props.translate}
            />;

        if (this.hasNothingToList(overviewEnabled)) {
            // the switch stays reachable, so an empty beta view can still be left
            return (
                <>
                    {betaSwitch}
                    <NoTasks translate={this.props.translate}/>
                </>
            );
        }

        return (
            <div
                className={"ast_task_list " + ((this.props.hiddenTasks > 0 || this.props.batchTasks) ? "task_list__hidden" : "")}>
                {this.props.hiddenTasks > 0  &&
                    <section><HiddenTasksWarning hiddenTasks={this.props.hiddenTasks}
                                                 translate={this.props.translate}/></section>
                }
                {this.props.batchTasks &&
                    <section className="batch-tasks mb-4">
                        <NewPaymentBatch batch={this.props.batchTasks}
                                         translate={this.props.translate}/>
                    </section>
                }

                <AsyncTaskActionExecutor translate={this.props.translate}
                                         onSentForReview={this.loadReviewTasks}/>
                <Search updateSearchCallback={this.props.changeSearchFilter}
                        updateCompanyFilterCallback={this.changeCompanyFilter}
                        searchValue={this.props.search}
                        onlyCurrentCompany={this.props.company}
                        oneCompanyOnlyUser={oneCompanyOnlyUser}
                        displayedDate={this.props.displayedDate}
                        changeDisplayedDate={this.props.changeDisplayedDate}
                        overviewEnabled={overviewEnabled}
                        groupBy={this.state.groupBy}
                        onGroupByChange={this.changeGroupBy}
                        columns={overviewEnabled ? this.getColumnOptions() : null}
                        onColumnToggle={this.toggleColumn}
                        onColumnsReset={this.resetColumns}
                        columnsCustomised={this.isGridCustomised()}
                        csvExportEnabled={this.props.showCsvExport}
                        onExportCsv={this.exportCsv}
                        exportDisabled={this.props.tasks.filteredTasks.length === 0}
                />
                <main className={this.getContentClassName(overviewEnabled)}>
                    {overviewEnabled ?
                        // ag-grid renders its own column header and owns the scroll area
                        this.renderGrid() :
                        this.renderFlatList()
                    }

                    <TableFooter/>
                    {betaSwitch}
                    <TasksActionButtons
                        selectedTaskKeys={this.props.tasks.selectedKeys}
                        userSettings={this.props.userConfiguration.userSettings}
                        tasks={this.props.tasks.filteredTasks}
                        action={this.props.taskAction}
                        actions={activeActions}
                        pendingTotals={overviewEnabled ? sumTotalAmount(this.props.tasks.filteredTasks) : null}
                    />

                </main>
            </div>
        );
    }

    render() {
        switch (this.props.loadingStatus) {

            case LOADING_STATUS.LOADING:
                return (
                    <div className="position-fixed top-50 w-100">
                        <Loader size={LoaderSize.Large} />
                    </div>
                );

            case LOADING_STATUS.ERROR:
                return (
                    <ServerError msg={this.props.tasks}/>
                );


            case LOADING_STATUS.DONE:
                return this.renderTasks();

            default:
                // pass translate, otherwise it creates another connected component, which break the unit testing of myTasks.component
                return <ServerError translate={this.props.translate}/>;
        }

    }
}

MyTasks.propTypes = {
    userConfiguration: PropTypes.shape({
        userSettings: PropTypes.object,
    }),
    availableContextsCount: PropTypes.number,
    tasks: PropTypes.shape({
        allTasks: PropTypes.array,
        filteredTasks: PropTypes.array,
        selectedTasks: PropTypes.array,
        selectedKeys: PropTypes.array,
        areAllSelected: PropTypes.bool,
    }),
    loadingStatus: PropTypes.string,
    sort: PropTypes.object,
    search: PropTypes.string,
    company: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    scrollTop: PropTypes.number,
    hiddenTasks: PropTypes.number,
    displayedDate: PropTypes.string,
    batchTasks: PropTypes.object,
    tasksWithPayment: PropTypes.array,
    showLastChangedBy: PropTypes.bool,
    showBetaSwitch: PropTypes.bool,
    showCsvExport: PropTypes.bool,
    showQuickView: PropTypes.bool,
    showTasksOnReview: PropTypes.bool,
    currentContextId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    translate: PropTypes.func.isRequired,
    changeTaskSelection: PropTypes.func.isRequired,
    changeTasksSelection: PropTypes.func.isRequired,
    changeAllTaskSelection: PropTypes.func.isRequired,
    changeSort: PropTypes.func.isRequired,
    changeSearchFilter: PropTypes.func.isRequired,
    changeCompanyFilter: PropTypes.func.isRequired,
    loadTasks: PropTypes.func.isRequired,
    changeOneHandleOnlyFlag: PropTypes.func.isRequired,
    changeTryToLoopTasksFlag: PropTypes.func.isRequired,
    actionFinished: PropTypes.func.isRequired,
    taskAction: PropTypes.func.isRequired,
    saveScrollPosition: PropTypes.func.isRequired,
    changeDisplayedDate: PropTypes.func.isRequired,
    changeAutopayURL: PropTypes.func.isRequired,
    changeVisibleOrder: PropTypes.func.isRequired,
};

const withTranslations = translate(MyTasks);

const mapStateToProps = function (store) {
    return {
        userConfiguration: reduxSelectors.getUsersData(store),
        currentContextId: reduxSelectors.getUsersCurrentContextId(store),
        availableContextsCount: reduxSelectors.getAvailableContextsCount(store),
        tasks: reduxSelectors.getTasksForRender(store),
        loadingStatus: reduxSelectors.getLoadingStatus(store),
        sort: reduxSelectors.getSort(store),
        search: reduxSelectors.getFilterText(store),
        company: reduxSelectors.getFilterCompany(store),
        actionParameters: reduxSelectors.getActionParameters(store),
        scrollTop: reduxSelectors.getScrollPosition(store),
        hiddenTasks: reduxSelectors.getHiddenTasks(store),
        displayedDate: reduxSelectors.getDisplayedDate(store),
        batchTasks: reduxSelectors.getActiveBatch(store),
        tasksWithPayment: reduxSelectors.getTasksWithPayment(store),
        showLastChangedBy: reduxSelectors.getShowLastChangedBy(store)
    };
};


const Connected = connect(mapStateToProps, {
    changeTaskSelection,
    changeTasksSelection,
    changeAllTaskSelection,
    changeSort,
    changeSearchFilter,
    changeCompanyFilter,
    loadTasks,
    changeOneHandleOnlyFlag,
    changeTryToLoopTasksFlag,
    actionFinished,
    taskAction,
    loadHistory,
    saveScrollPosition,
    changeDisplayedDate,
    changeAutopayURL,
    changeVisibleOrder
})(withTranslations);

// Per-tenant flag offering the redesigned overview (grouping + column picker)
// next to the classic list, picked with the switch; default false keeps the current flat list
// as production behavior.
const MyTasksWithFlag = (props) => {
    const showBetaSwitch = launchDarkly.companyClient?.variation("show-my-tasks-beta-switch", false);
    // a gate of its own inside the beta, so the export can be rolled out to a tenant already on it
    const showCsvExport = launchDarkly.companyClient?.variation("show-my-tasks-csv-export", false);
    // likewise for the task quick view drawer
    const showQuickView = launchDarkly.companyClient?.variation("show-my-tasks-quick-view", false);
    // the tasks the user sent on review, as rows of the new version; off until the backend is everywhere
    const showTasksOnReview = launchDarkly.companyClient?.variation("fe-show-my-tasks-on-review", false);
    return <Connected {...props} showBetaSwitch={showBetaSwitch} showCsvExport={showCsvExport}
                      showQuickView={showQuickView} showTasksOnReview={showTasksOnReview}/>;
};

/**
 * whether two lists of wrappers hold the same ones, in the same order - filtering hands out a new
 * array on every render, and a regrouping for each of them would re-render the grid as often
 */
function sameWrappers(first = [], second = []) {
    return first.length === second.length && first.every((wrapper, index) => wrapper === second[index]);
}

export default MyTasksWithFlag;


const NoTasks = (props) => {
    return (
        <div className="no-tasks-message text-center ast_task_list">
            <span className="col-md-12 icon icon-48 smiley-face"/>
            <span
                className="col-md-10 text-left margin-bottom"><b>{props.translate("myTasks.allTasksHandled")}</b></span>
            <br/>
            <span className="col-md-10 text-left">{props.translate("myTasks.notifyYou")}</span>
        </div>)
};

NoTasks.propTypes = {
    translate: PropTypes.func.isRequired,
};

const NoTasksFound = (props) => {
    return (
        <div className="no-tasks-found text-disabled">
            {props.translate('myTasks.noTasksFound')}
        </div>
    )
};

NoTasksFound.propTypes = {
    translate: PropTypes.func.isRequired,
};

const HiddenTasksWarning = (props) => {
    let message = props.hiddenTasks > 1 ? "myTasks.hiddenTasksWarning" : "myTasks.hiddenTaskWarning";
    let licenseMessage = props.hiddenTasks > 1 ? "myTasks.loginLinkPlural" : "myTasks.loginLinkSingular";
    return (
        <div className="d-flex justify-content-center my-3">
            <Notification type={NotificationType.INFORMATION}
                          description={
                              <div>
                                  {props.translate(message, props.hiddenTasks)}
                                  <a href="#" onClick={login}>{props.translate(licenseMessage)}</a>
                                  {props.translate("myTasks.licenseWarning")}
                              </div>
                          }/>
        </div>
    )
};

HiddenTasksWarning.propTypes = {
    hiddenTasks: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
};